// frontend/src/pages/customer/FreelancerLanding.js
// /freelancer — public marketing page for independent beauty professionals,
// restyled to mirror the premium OGESTYLE landing (shares the fb- design
// system from ForBusinesses.css). Hero/section photos are /landing/*.jpg
// placeholders — swap for final provided photography.

import React from "react";
import { Link, useNavigate } from "react-router-dom";
import CustomerHeader from "../../components/CustomerHeader";
import CustomerFooter from "../../components/CustomerFooter";
import {
  FaUserTie,
  FaMoneyBillWave,
  FaCalendarAlt,
  FaMobileAlt,
  FaShoppingBag,
  FaChartLine,
  FaCheckCircle,
  FaArrowRight,
  FaStore,
  FaHome,
} from "react-icons/fa";
import "../ForBusinesses.css";
import ProtectRevenue from "../../components/ProtectRevenue";

const IMG = {
  freelance: "/landing/freelancer-hero.jpg",
  salon: "/landing/salon.jpg",
};

const SUPPORT_EMAIL = "support@mg.ogestyle.com";

const trust = [
  "Keep more of your earnings",
  "Set your own hours & prices",
  "No shop overhead",
];

const benefits = [
  {
    icon: <FaUserTie />,
    title: "Your own brand",
    desc: "Work independently with your own profile and Looks — no shop required.",
  },
  {
    icon: <FaMoneyBillWave />,
    title: "Keep more earnings",
    desc: "Set your own prices and keep more of what you earn.",
  },
  {
    icon: <FaCalendarAlt />,
    title: "Flexible schedule",
    desc: "Set your own hours and stay in control of your calendar.",
  },
  {
    icon: <FaMobileAlt />,
    title: "Mobile services",
    desc: "Offer at-home or mobile services, or work from your own studio.",
  },
  {
    icon: <FaShoppingBag />,
    title: "Sell products",
    desc: "Build your own catalog and sell directly to your clients.",
  },
  {
    icon: <FaChartLine />,
    title: "Business analytics",
    desc: "Track earnings, bookings and growth with clear insights.",
  },
];

const features = [
  "Online booking system",
  "Client management",
  "Service catalog",
  "Product sales",
  "Payment processing",
  "Schedule management",
  "Earnings tracking",
  "Client notifications",
];

const freelancerChecks = [
  "Work independently without a physical shop",
  "Offer mobile or at-home services",
  "Want to keep more of your earnings",
  "Prefer flexible scheduling",
  "Don't have a registered business license",
];

const businessChecks = [
  "Have a registered business with a license",
  "Operate one or multiple physical locations",
  "Need to manage staff and employees",
  "Run a salon, spa, or barbershop",
];

function FreelancerLanding() {
  const navigate = useNavigate();

  return (
    <>
      <CustomerHeader />
      <main className="fb-page">
        {/* Hero */}
        <section className="fb-hero">
          <div className="fb-wrap fb-hero-grid">
            <div>
              <p className="fb-eyebrow">For independent professionals</p>
              <h1 className="fb-display">Build your independent beauty business</h1>
              <p className="fb-sub">
                Independent hairstylists, makeup artists and estheticians — run
                your own brand without the overhead of a shop.
              </p>
              <div className="fb-ctas">
                <button
                  type="button"
                  className="fb-btn fb-btn-primary"
                  onClick={() => navigate("/freelancer-signup")}
                >
                  Start free today
                </button>
                <a className="fb-link" href={`mailto:${SUPPORT_EMAIL}`}>
                  Talk to us
                </a>
              </div>
            </div>

            <div className="fb-hero-art">
              <span className="fb-blob" />
              <img
                className="fb-hero-img"
                src={IMG.freelance}
                alt="An independent beauty professional at work"
              />
              <div className="fb-chip-card">
                <span className="fb-chip-ic"><FaHome /></span>
                <div>
                  <p className="fb-chip-title">Work your way</p>
                  <p className="fb-chip-sub">At home, on the go, or at clients</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Trust chips */}
        <section className="fb-wrap fb-trust" aria-label="Highlights">
          {trust.map((t) => (
            <span key={t}><FaCheckCircle /> {t}</span>
          ))}
        </section>

        {/* Benefits */}
        <section className="fb-sec fb-sec--white">
          <div className="fb-wrap">
            <div className="fb-sec-head">
              <p className="fb-eyebrow">Why freelance with OGESTYLE</p>
              <h2 className="fb-display">Everything you need to succeed</h2>
            </div>
            <div className="fb-grid">
              {benefits.map((b) => (
                <article className="fb-card" key={b.title}>
                  <span className="fb-ic">{b.icon}</span>
                  <h3>{b.title}</h3>
                  <p>{b.desc}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Features (navy) */}
        <section className="fb-navy">
          <div className="fb-wrap">
            <p className="fb-eyebrow">Platform features</p>
            <h2 className="fb-display">Built for independent pros</h2>
            <div className="fb-feat">
              {features.map((f) => (
                <div key={f}><FaCheckCircle /> {f}</div>
              ))}
            </div>
          </div>
        </section>

        {/* Protect your revenue */}
        <ProtectRevenue />

        {/* Compare */}
        <section className="fb-sec fb-sec--white">
          <div className="fb-wrap">
            <div className="fb-sec-head fb-center">
              <p className="fb-eyebrow">Which account is right for you?</p>
              <h2 className="fb-display">Freelancer or business owner?</h2>
            </div>
            <div className="fb-compare">
              <article className="fb-panel fb-panel--navy">
                <h3>Choose Freelancer if you…</h3>
                <ul>
                  {freelancerChecks.map((c) => (
                    <li key={c}><FaCheckCircle /> {c}</li>
                  ))}
                </ul>
                <button
                  type="button"
                  className="fb-btn fb-btn-light"
                  onClick={() => navigate("/freelancer-signup")}
                >
                  Start free today
                </button>
              </article>

              <article className="fb-panel fb-panel--cream">
                <div className="fb-panel-img">
                  <img src={IMG.salon} alt="A beauty salon interior" />
                </div>
                <h3>Choose Business Owner if you…</h3>
                <ul>
                  {businessChecks.map((c) => (
                    <li key={c}><FaCheckCircle /> {c}</li>
                  ))}
                </ul>
                <Link className="fb-panel-link" to="/for-businesses">
                  Learn about business accounts <FaArrowRight />
                </Link>
              </article>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="fb-wrap fb-cta-wrap">
          <div className="fb-cta-band">
            <div>
              <h2 className="fb-display">Ready to start your journey?</h2>
              <p className="fb-cta-desc">
                Create your free account in under 30 minutes — no contract.
              </p>
            </div>
            <div className="fb-cta-actions">
              <button
                type="button"
                className="fb-btn fb-btn-primary"
                onClick={() => navigate("/freelancer-signup")}
              >
                Create free account
              </button>
              <a
                className="fb-btn fb-btn-outline"
                href="https://www.ogestyle.com/demo"
                rel="noreferrer noopener"
              >
                Book a live walkthrough
              </a>
            </div>
          </div>
        </section>
      </main>
      <CustomerFooter />
    </>
  );
}

export default FreelancerLanding;
